import type { BoxProps } from '@talos/kyoko';
import {
  BlotterDensity,
  BlotterTable,
  DEFAULT_BLOTTER_COLDEF,
  DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  Input,
  MarketTypeEnum,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  ThemeTypes,
  Toggle,
  useBehaviorSubject,
  useBlotterTable,
  useCallbackRef,
  useCurrentWindow,
  useMixpanel,
  useTabs,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { GridOptions, IAggFuncParams, RowClassParams, RowClickedEvent } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { Fragment, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useMarketSelectorContext } from '../MarketSelectorContext';
import { MarketSelectorSelectionSummary } from '../MarketSelectorSelectionSummary';
import { MarketSelectorSettingsButton } from '../MarketSelectorSettingsButton';
import { MARKET_SELECTOR_BLOTTER_TAB_SIZE, MARKET_SELECTOR_POPOUT_WINDOW_NAME } from '../tokens';
import { AvailabilityEnum } from '../types';
import { isMarketSelectorItemSelectable } from '../utils/items';
import { MarketSelectorBlotterActionItems } from './MarketSelectorBlotterActionItems';
import type { MarketSelectorCheckboxValue } from './MarketSelectorBlotterCheckboxCellRenderer';
import { isMarketParentRowSelectable, marketSelectorAutoGroupColumnDef } from './MarketSelectorGroupColumn';
import { StyleWrapper } from './styles';
import { ALL_TAB, DARK_TAB, DEALERS_TAB, EXCHANGES_TAB, type MarketSelectorTab } from './tabs';
import type { MarketSelectorRow } from './types';
import {
  MARKET_SELECTOR_CHECKBOX_AGG_FUNC,
  MARKET_SELECTOR_CHECKBOX_COLID,
  useMarketSelectorColumns,
} from './useMarketSelectorColumns';

export const MarketSelectorBlotter = ({
  onSelect,
  isMobile = false,
  ...boxProps
}: BoxProps & { isMobile?: boolean }) => {
  const {
    showDisabledItems,
    items,
    selectionsSet,
    setSelected,
    enrichments,
    extensions,
    selectMarket,
    credentialsOnly,
    advancedMode,
    setBlotterSearchInput,
  } = useMarketSelectorContext();
  const mixpanel = useMixpanel();

  // These tabs are specific to the view, so they're built and referenced out here in the blotter component as opposed to in the model.
  const tabsItems: MarketSelectorTab[] = useMemo(() => {
    const darkExists = items.some(item => item.market.Type === MarketTypeEnum.Dark);
    return compact([ALL_TAB, EXCHANGES_TAB, DEALERS_TAB, darkExists && DARK_TAB]);
  }, [items]);

  const tabs = useTabs({
    initialItems: tabsItems,
    initialSelectedIndex: 0,
  });

  const selectedTab = tabs.items[tabs.selectedIndex];

  // Go from MarketSelectorItems to more specific Blotter entities.
  // TODO: convert this to a delta model
  const blotterRows: MarketSelectorRow[] = useMemo(() => {
    const rows: MarketSelectorRow[] = [];
    for (const item of items) {
      const row: MarketSelectorRow = {
        item,
        key: item.name,

        // we attach selected to the blotter entity because we want the row's change detection to run when selection state changes without hacking anything
        // we _could_ avoid this and just have the cells resolve their selected state from the market selector context, but we opt to do it this way instead
        selected: selectionsSet.has(item.name),

        enrichments: {},
        extensionData: {},
      };

      if (!showDisabledItems) {
        // We filter out all items that have Availability <= Disabled, except for the case where they are already selected.
        // We leave them around such that the user can unselect them explicitly to avoid confusion.
        if (!row.selected && row.item.availability.availability <= AvailabilityEnum.Disabled) {
          continue; // filter out item
        }
      }

      if (credentialsOnly) {
        if (row.item.marketAccount == null) {
          continue; // filter out item
        }
      }

      if (selectedTab.params.filter != null) {
        if (!selectedTab.params.filter(row.item)) {
          continue; // filter out item
        }
      }

      // Attach each of our enrichments to the row
      for (const enrichmentSpec of enrichments) {
        row.enrichments[enrichmentSpec.enrichmentKey] = enrichmentSpec.dataMap?.get(row.key);
      }
      for (const extension of extensions) {
        row.extensionData[extension.id] = extension.dataMap?.get(row.key);
      }

      rows.push(row);
    }

    return rows;
  }, [items, showDisabledItems, credentialsOnly, selectionsSet, selectedTab, enrichments, extensions]);

  const selectedRows = useMemo(() => blotterRows.filter(row => row.selected), [blotterRows]);

  const { observable: dataObservable } = useBehaviorSubject(() => {
    // Convert our data array into an observable
    // In the future, we will want to send delta messages here when it comes to showing real-time data
    return {
      type: '',
      ts: '',
      data: blotterRows,
      initial: true,
    } satisfies MinimalSubscriptionResponse<MarketSelectorRow>;
  }, [blotterRows]);

  const handleRowClicked = useCallbackRef((event: RowClickedEvent<MarketSelectorRow>) => {
    const clickedColumnID = event.event
      ?.composedPath()
      .find((target): target is Element => target instanceof Element && target.hasAttribute('col-id'))
      ?.getAttribute('col-id');
    const interactiveColumnIDs = new Set(extensions.flatMap(extension => extension.interactiveColumnIDs ?? []));
    if (clickedColumnID && interactiveColumnIDs.has(clickedColumnID)) {
      return;
    }

    const row = event.data;
    if (!row) {
      // we're a group row
      const marketName = event.node.key;
      if (marketName) {
        const checkboxCellValue: MarketSelectorCheckboxValue | null | undefined = event.api.getCellValue({
          rowNode: event.node,
          colKey: MARKET_SELECTOR_CHECKBOX_COLID,
        });
        const groupRowChecked = checkboxCellValue?.selected ?? false;
        selectMarket(marketName, !groupRowChecked);
      }
      return;
    }

    // The user cant click the row to select a disabled item. However, if the item already is selected, the user can click to de-select.
    if (!isMarketSelectorItemSelectable(row.item) && !row.selected) {
      return;
    }

    setSelected(row.item.name, !selectionsSet.has(row.item.name));
  });

  const gridOptions: GridOptions = useMemo(
    () => ({
      rowClassRules: {
        'market-selector-row-selected': (params: RowClassParams<MarketSelectorRow>) => {
          if (params.node.data != null) {
            return !!params.node.data?.selected;
          }

          return params.node.allLeafChildren?.some(child => child.data?.selected) ?? false;
        },
        'market-selector-row-disabled': (params: RowClassParams<MarketSelectorRow>) => {
          if (params.node.group) {
            return !isMarketParentRowSelectable(params.node);
          }

          const availability = params.node.data?.item.availability.availability;
          if (availability == null) {
            return false;
          }

          return availability <= AvailabilityEnum.Disabled;
        },
      },
      aggFuncs: {
        [MARKET_SELECTOR_CHECKBOX_AGG_FUNC]: ({
          values,
        }: IAggFuncParams<MarketSelectorRow, MarketSelectorCheckboxValue>) => {
          return { selected: values.some(value => value?.selected === true) };
        },
      },
      onRowClicked: handleRowClicked,
      onRowDataUpdated: event => event.api.refreshHeader(),
      autoGroupColumnDef: marketSelectorAutoGroupColumnDef,
      groupHideParentOfSingleChild: true,
      // We handle selection on our own in this component, as in we don't rely on any AgGrid selection functionality.
      // Even the checkboxes are our own rendered components.
      rowSelection: DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
      groupDefaultExpanded: -1,
      groupMaintainOrder: false, // when sorting on non-grouped columns, will change the order of the groups
      defaultColDef: {
        suppressHeaderMenuButton: true,
        ...DEFAULT_BLOTTER_COLDEF,
      },
      suppressContextMenu: true,
      rowHeight: 40,
      autoSizeStrategy: {
        type: 'fitGridWidth',
      },
    }),
    [handleRowClicked]
  );

  const columns = useMarketSelectorColumns({ advancedMode, enrichments, extensions });

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable: undefined,
    rowID: 'key' satisfies keyof MarketSelectorRow,
    columns,
    gridOptions,
    // MarketSelector cells contain controlled checkboxes. Async transactions let the clicked input flip immediately
    // while the rest of the grid waits for AG Grid's batch flush, which makes column-level checkbox updates look laggy.
    applyTransactionsSynchronously: true,
  });

  const { type } = useTheme();
  const isDark = type === ThemeTypes.dark;

  return (
    <Tabs
      {...tabs}
      size={MARKET_SELECTOR_BLOTTER_TAB_SIZE}
      flex={1}
      data-testid="market-selector-panel-blotter"
      {...boxProps}
    >
      <TabList
        isBordered
        rowEndSpacing="spacing16"
        rightItems={<MarketSelectorBlotterRightItems showActionItems={!isMobile} isMobile={isMobile} />}
      >
        {tabs.items.map((tab, index) => (
          <Tab key={index} {...tab} />
        ))}
      </TabList>
      <TabPanel display="flex" flexDirection="column" flex={1}>
        <Input
          prefix={<Icon icon={IconName.Search} />}
          placeholder="Search..."
          clearable={true}
          value={blotterTable.blotterTableFiltersProps.quickFilterText}
          onChange={e => {
            mixpanel.track(MixpanelEvent.FilterQuickSearch, {
              [MixpanelEventProperty.Source]: MixpanelEventSource.MarketAccountSelector,
            });
            blotterTable.blotterTableFiltersProps.onQuickFilterTextChanged(e.target.value);
          }}
          data-testid="market-selector-panel-search"
          ref={setBlotterSearchInput}
          style={{
            border: 'none',
            borderRadius: '0',
          }}
        />
        <StyleWrapper h="100%">
          <BlotterTable
            {...blotterTable}
            density={BlotterDensity.Default}
            background={isDark ? 'colors.gray.040' : 'backgroundDropdown'}
          />
        </StyleWrapper>
        <MarketSelectorSelectionSummary rows={selectedRows} />
      </TabPanel>
    </Tabs>
  );
};

function MarketSelectorBlotterRightItems({
  showActionItems,
  isMobile,
}: {
  showActionItems: boolean;
  isMobile: boolean;
}) {
  const { showAdvancedMode, advancedMode, setAdvancedMode, isNarrow, extensions } = useMarketSelectorContext();
  const currentWindow = useCurrentWindow();
  const isPopout = currentWindow.name === MARKET_SELECTOR_POPOUT_WINDOW_NAME;
  const mixpanel = useMixpanel();
  const showModeControls = !isNarrow || isPopout;

  return (
    <HStack gap="spacing12">
      {showModeControls &&
        extensions.map(extension => <Fragment key={extension.id}>{extension.renderBlotterControl?.()}</Fragment>)}
      {showModeControls && showAdvancedMode && (
        <Toggle
          size={FormControlSizes.Small}
          checked={advancedMode}
          onChange={checked => {
            setAdvancedMode(checked);
            if (isPopout && !checked) {
              currentWindow.close();
            }
            mixpanel.track(MixpanelEvent.ToggleAdvancedMode, { [MixpanelEventProperty.Enabled]: checked });
          }}
          label="Advanced Mode"
          data-testid="market-selector-advanced-mode-toggle"
        />
      )}
      {isMobile && <MarketSelectorSettingsButton mobile />}
      {showActionItems && <MarketSelectorBlotterActionItems />}
    </HStack>
  );
}
